import React, { useRef } from "react";

import { DeleteOutline } from "@mui/icons-material";
import {
  Box,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import GooglePlacesAutocomplete from "@/common/atomic-UI-components/GooglePlaceAutocomplete";
import { LocationErrors } from "@/components/alerts/add-v2/model/locationRows";
import { BulletinLocation } from "@/components/alerts/add-v2/model/types";
import { LOCATION_CLASSIFICATION } from "@/components/alerts/constants";
import { PlaceType } from "@/types";

import { geocodePlace } from "./geocodePlace";
import { FIELD_ROW_SX, ROW_DELETE_SX, TYPE_COL_SX } from "./rowLayout";

/**
 * One location row: a heading, the type select, the Google Places search, and the remove button, with
 * a "specify type" field appearing underneath when the type is Other.
 *
 * **Layout taken from the design prototype's own styles**, and identical to `IncidentTimeRow`'s
 * because the prototype reuses the same objects for both — the shared parts live in `rowLayout.ts`.
 * The address column takes `flex: 1`, and the Other field sits on its own line at the prototype's
 * `otherTypeColStyle` (`width: 30%, minWidth: 180px`).
 *
 * **The type select is always present and always enabled.** The old `AddressList` hides it behind
 * `display: place_id ? "flex" : "none"` and disables it until an address has been picked, which is the
 * behaviour this design drops: an officer can say what kind of address they are about to enter before
 * they have found it.
 *
 * `AddressList` is not reused for the reasons in README decision 7 — it owns its own list and its own
 * "Add another location", speaks the legacy `PlaceType` row shape with `location_classification`
 * baked in, carries its own `{ isValid, hasLocationType }` error shape, and has no place for
 * `resolved`. What is reused is the piece worth reusing: `GooglePlacesAutocomplete`, which carries the
 * Places billing-session handling (a session token per search, closed with a minimal `getDetails`
 * call so the predictions leading up to a selection are not billed separately).
 */

const OTHER_COL_SX = {
  width: { xs: "100%", sm: "30%" },
  minWidth: { sm: 180 },
};

interface LocationRowProps {
  value: BulletinLocation;
  /** 1-based, for the heading and the address label. Rows are numbered as displayed, never stored. */
  index: number;
  /**
   * A patch rather than a whole row, because the geocode lands asynchronously: by the time it does the
   * officer may have changed the type, and writing back a row captured before the request would undo
   * that. The section merges each patch against the current value.
   */
  onPatch: (patch: Partial<BulletinLocation>) => void;
  onRemove: () => void;
  /** False for the last remaining row — every row that exists is required. */
  removable: boolean;
  /**
   * What `validateLocationRow` found, or `{}` while this row is too new to complain about. The
   * section decides which, because "when to show an error" is a page-wide rule and "what is wrong"
   * is a per-row one.
   */
  errors: LocationErrors;
}

export const LocationRow: React.FC<LocationRowProps> = ({
  value,
  index,
  onPatch,
  onRemove,
  removable,
  errors,
}) => {
  const { t } = useTranslation("bulletin/create-v2");

  const message = (code: string | undefined) =>
    code ? t(`validation.location_${code}`) : undefined;

  /**
   * The id of the selection a geocode is being awaited for. Two selections in quick succession
   * can settle out of order, and the row would then hold an `input` and a `resolved` describing
   * two different places — a mismatched pair that autosave and finalize both persist. The
   * latest selection is the only one allowed to write.
   */
  const pending = useRef<string | null>(null);

  const selectAddress = async (next: PlaceType | null) => {
    // The prediction lands immediately so the field stops feeling laggy; `resolved` is cleared because
    // whatever was there described the previous address.
    onPatch({ input: next, resolved: null });

    if (!next?.place_id) {
      pending.current = null;
      return;
    }

    pending.current = next.place_id;
    const resolved = await geocodePlace(next.place_id);
    if (resolved && pending.current === next.place_id) onPatch({ resolved });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.4 }}>
        {t("fields.location", { index })}
      </Typography>

      <Box sx={FIELD_ROW_SX}>
        <TextField
          select
          size="small"
          required
          error={!!errors.classification}
          helperText={message(errors.classification)}
          label={t("fields.location_type")}
          value={value.classification ?? ""}
          sx={TYPE_COL_SX}
          onChange={(event) => {
            const classification = event.target
              .value as LOCATION_CLASSIFICATION;
            onPatch({
              classification,
              // The free-text type only means anything under Other, so moving away from it drops what
              // was typed rather than carrying a value no input shows into the payload.
              ...(classification === LOCATION_CLASSIFICATION.OTHER
                ? {}
                : { otherClassification: "" }),
            });
          }}
        >
          {Object.values(LOCATION_CLASSIFICATION).map((classification) => (
            <MenuItem key={classification} value={classification}>
              {classification}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <GooglePlacesAutocomplete
            // Distinct per row: the id is what ties the label to the input, and several rows share
            // this component.
            id={`bulletin-location-${value.id}`}
            label={t("fields.address", { index })}
            placeholder={t("fields.address_placeholder")}
            required
            selectedAddress={value.input}
            setSelectedAddress={selectAddress}
            error={!!errors.input}
            helperText={message(errors.input)}
          />
        </Box>

        <IconButton
          aria-label={t("fields.remove_row")}
          disabled={!removable}
          onClick={onRemove}
          sx={ROW_DELETE_SX}
        >
          <DeleteOutline />
        </IconButton>
      </Box>

      {value.classification === LOCATION_CLASSIFICATION.OTHER && (
        <TextField
          size="small"
          required
          error={!!errors.otherClassification}
          helperText={message(errors.otherClassification)}
          label={t("fields.other_location_type")}
          value={value.otherClassification}
          sx={OTHER_COL_SX}
          onChange={(event) =>
            onPatch({ otherClassification: event.target.value })
          }
        />
      )}
    </Box>
  );
};
